import { useCallback, useMemo } from 'react';
import { EntityName } from 'record-it-time-common/EntityName';
import { ArrayUtils } from 'record-it-utils/utils/ArrayUtils/ArrayUtils';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { ConstructionSite } from '../ConstructionSite/constructionSiteTypes';
import { ConstructionSiteUsageStatistic } from './constructionSiteUsageStatisticTypes';

export function useConstructionSiteUsageStatisticTools(): ConstructionSiteUsageStatisticTools {
  const repository = useEntityRepository(
    EntityName.ConstructionSiteUsageStatistic
  );
  const { getRequiredCurrentUser } = useCurrentUser();

  const getConstructionSitesWithStatistics = useCallback(
    (constructionSites: Array<ConstructionSite>) => {
      const statistics = repository.getByUserIdAndConstructionSiteIds(
        getRequiredCurrentUser().id,
        constructionSites.map((cs) => cs.id)
      );
      const statisticsByConstructionSiteId = ArrayUtils.key(
        statistics,
        (statistic) => statistic.constructionSiteId
      );

      return constructionSites
        .map((cs) => {
          return {
            constructionSite: cs,
            statistic: statisticsByConstructionSiteId.get(cs.id)
          };
        })
        .filter(
          (
            constructionSiteWithStatistic
          ): constructionSiteWithStatistic is ConstructionSiteWithStatistic => {
            return !!constructionSiteWithStatistic.statistic;
          }
        );
    },
    [repository, getRequiredCurrentUser]
  );

  return useMemo(
    () => ({
      usedConstructionSite: (constructionSite) => {
        const userId = getRequiredCurrentUser().id;
        const statistic = repository.getByUserIdAndConstructionSiteId(
          userId,
          constructionSite.id
        );

        if (statistic) {
          repository.update({
            ...statistic,
            usageCount: statistic.usageCount + 1,
            lastUsedDate: new Date().toISOString()
          });
        } else {
          repository.create({
            userId,
            constructionSiteId: constructionSite.id,
            usageCount: 1,
            lastUsedDate: new Date().toISOString()
          });
        }
      },
      getMostUsedConstructionSites: (constructionsSites) => {
        const constructionSitesWithStatistic =
          getConstructionSitesWithStatistics(constructionsSites).filter(
            (constructionSiteWithStatistic) =>
              constructionSiteWithStatistic.statistic.usageCount > 0
          );

        constructionSitesWithStatistic.sort((a, b) => {
          const usageDiff = b.statistic.usageCount - a.statistic.usageCount;

          if (usageDiff !== 0) {
            return usageDiff;
          }

          return a.constructionSite.name.localeCompare(b.constructionSite.name);
        });

        return constructionSitesWithStatistic.map(
          (constructionSiteWithStatistic) =>
            constructionSiteWithStatistic.constructionSite
        );
      }
    }),
    [getRequiredCurrentUser, repository, getConstructionSitesWithStatistics]
  );
}

export type ConstructionSiteUsageStatisticTools = {
  usedConstructionSite: (constructionSite: ConstructionSite) => void;
  /**
   * Returns the construction sites which have been used at least once, sorted by the usageCount.
   * If 2 construction sites have the same usage count, they will be sorted by name then
   */
  getMostUsedConstructionSites: (
    constructionSites: Array<ConstructionSite>
  ) => Array<ConstructionSite>;
};

type ConstructionSiteWithStatistic = {
  constructionSite: ConstructionSite;
  statistic: ConstructionSiteUsageStatistic;
};
